/**
 * SEO Component
 * Injects page-specific meta tags + JSON-LD directly into <head>.
 * (react-helmet-async in this project reliably manages only <title>, so we
 *  inject description/canonical/OG/schema via useEffect to guarantee they apply.)
 */

import { useEffect } from 'react';

const BASE_URL = 'https://drdnreddyortho.com';
const SITE_NAME = 'Dr. Deepthi Nandan Reddy - Upper Limb Surgeon';
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_664f5b05-f9bf-427d-869f-0304c630b7f5/artifacts/d8vss7a1_e146c649-5e7e-46df-a58e-0e1fdc565827.avif';

const upsertMeta = (attr, key, content) => {
  if (content == null) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

export const SEO = ({ 
  title,
  description,
  keywords,
  path = '',
  image = DEFAULT_IMAGE,
  type = 'website',
  schema = null
}) => {
  const fullUrl = `${BASE_URL}${path}`;
  const fullTitle = title 
    ? (/reddy/i.test(title) ? title : `${title} | Dr. Deepthi Nandan Reddy`)
    : 'Dr. Deepthi Nandan Reddy – Orthopedic & Sports Injury Surgeon, Apollo Hospitals Hyderabad';

  useEffect(() => {
    document.title = fullTitle;

    upsertMeta('name', 'description', description);
    if (keywords) upsertMeta('name', 'keywords', keywords);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = fullUrl;

    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:url', fullUrl);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'og:site_name', SITE_NAME);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:url', fullUrl);
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);

    let schemaEl = null;
    if (schema) {
      schemaEl = document.createElement('script');
      schemaEl.type = 'application/ld+json';
      schemaEl.setAttribute('data-seo-jsonld', path || 'home');
      schemaEl.textContent = JSON.stringify(schema);
      document.head.appendChild(schemaEl);
    }
    return () => { if (schemaEl) schemaEl.remove(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullTitle, description, fullUrl, keywords, type, image]);

  return null;
};


// Pre-defined SEO configs for each page
export const SEO_CONFIG = {
  home: {
    title: null,
    description: 'Fellowship-trained orthopedic surgeon at Apollo Hospitals Financial District, Hyderabad. Shoulder, knee, hip, elbow & sports injuries. Book a consultation.',
    keywords: 'orthopedic surgeon hyderabad, orthopedic doctor hyderabad, sports injury specialist hyderabad, shoulder surgeon hyderabad, knee replacement hyderabad, Apollo Hospitals Financial District, Dr Deepthi Nandan Reddy',
    path: '/',
    schema: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What conditions does Dr. Deepthi Nandan Reddy treat?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Dr. Reddy specializes in upper limb conditions including shoulder problems (rotator cuff tears, frozen shoulder, dislocations, arthritis), elbow conditions (tennis elbow, golfer's elbow, fractures), and hand/wrist issues (carpal tunnel syndrome, trigger finger, Dupuytren's contracture). He also treats sports injuries affecting the upper limb."
          }
        },
        {
          "@type": "Question",
          "name": "What are Dr. Reddy's qualifications?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Dr. Reddy holds MBBS, MS (Orthopaedics), MSc Orthopaedic Engineering (Cardiff, UK), FRCS (Trauma & Ortho, Edinburgh), FRCS (Ortho, UK), and CCT (UK). He completed fellowships at Mayo Clinic (USA), Florida Orthopaedic Institute (USA), and multiple UK centers including Pulvertaft Hand Centre."
          }
        },
        {
          "@type": "Question",
          "name": "Where is Dr. Deepthi Nandan Reddy's clinic located?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Dr. Reddy practices at Apollo Hospitals, Financial District, Hyderabad, Telangana 500084. Consultation hours are 9 AM to 5 PM, Monday through Saturday."
          }
        },
        {
          "@type": "Question",
          "name": "How can I book an appointment with Dr. Reddy?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You can book an appointment through Apollo Hospitals' online booking system, call +91 99599 64567, or send a WhatsApp message. Online booking is available 24/7 at apollohospitals.com."
          }
        },
        {
          "@type": "Question",
          "name": "Does Dr. Reddy perform arthroscopic surgery?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, Dr. Reddy is fellowship-trained in arthroscopic (keyhole) surgery of the shoulder, elbow, and wrist. Arthroscopic procedures include rotator cuff repair, Bankart repair for shoulder instability, elbow arthroscopy, and wrist arthroscopy for TFCC tears."
          }
        }
      ]
    }
  },
  about: {
    title: 'About – Upper Limb & Sports Surgeon, Hyderabad',
    description: 'Meet Dr. Deepthi Nandan Reddy – Senior Consultant Orthopaedic Surgeon at Apollo Hospitals, Hyderabad. 30+ years experience, UK CCT, FRCS (Edinburgh & UK), Mayo Clinic Fellow & President of the Shoulder & Elbow Society of India.',
    keywords: 'Dr Deepthi Nandan Reddy qualifications, upper limb surgeon credentials, FRCS surgeon india, Mayo clinic trained doctor, orthopaedic surgeon hyderabad',
    path: '/about'
  },
  testimonials: {
    title: 'Dr. Deepthi Nandan Reddy Reviews – Patient Testimonials',
    description: 'Real patient reviews & success stories for Dr. Deepthi Nandan Reddy (Dr. DN Reddy), Orthopedic Surgeon at Apollo Hospitals Financial District, Hyderabad. 5.0 Google rating.',
    keywords: 'dr deepthi nandan reddy reviews, Dr DN Reddy reviews, orthopedic surgeon reviews hyderabad, shoulder surgery testimonials, patient success stories',
    path: '/testimonials'
  },
  blog: {
    title: 'Upper Limb Care Blog',
    description: 'Expert articles on shoulder, elbow, and hand conditions by Dr. Deepthi Nandan Reddy. Learn about rotator cuff tears, frozen shoulder, tennis elbow, carpal tunnel.',
    keywords: 'shoulder care blog, rotator cuff information, frozen shoulder articles, tennis elbow treatment, carpal tunnel guide',
    path: '/blog'
  },
  resources: {
    title: 'Patient Resources: Recovery Guides, Exercises & FAQs',
    description: 'Free patient guides from Dr. DN Reddy, Apollo Hospitals Financial District: shoulder & knee exercises, pre-surgery prep, week-by-week recovery and FAQs. Book a consultation.',
    keywords: 'orthopedic patient resources hyderabad, shoulder exercise guide, rotator cuff exercises, post surgery recovery guide, frozen shoulder treatment guide',
    path: '/resources'
  },
  contact: {
    title: 'Contact & Book Appointment',
    description: 'Book an appointment with Dr. Deepthi Nandan Reddy at Apollo Hospitals, Financial District, Hyderabad. Call +91 99599 64567. Hours: 9 AM - 5 PM (Mon-Sat).',
    keywords: 'book appointment Dr DN Reddy, shoulder surgeon appointment hyderabad, Apollo Hospitals orthopaedic financial district',
    path: '/contact'
  }
};

export default SEO;
